/**
 * Argument form + confirmation for one runnable management command.
 *
 * The form is built from the command's declared argument specs, so adding an
 * argument in the backend registry surfaces it here with no frontend change.
 *
 * A destructive command makes the operator type its key before the Run button
 * enables. That is deliberate friction: these commands rewrite production data
 * and most have no undo beyond the one the command prints.
 */
import { useCallback, useMemo, useState } from 'react';
import { Loader2, Play, ShieldAlert } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import type { CommandArgSpec, RunnableCommandSpec } from '../../../lib/api';

type ArgValue = string | number | boolean;

interface CommandRunFormProps {
  command: RunnableCommandSpec;
  submitting: boolean;
  /** Values handed over from another command's findings. */
  prefill?: Record<string, ArgValue>;
  onRun: (args: Record<string, ArgValue>) => void;
}

/** A comma-separated list argument gets a textarea and a live count, not a
 *  single-line input showing 12 of 54 ids. */
function isListArg(arg: CommandArgSpec): boolean {
  return arg.type === 'str' && /ids$|lines$|list$/i.test(arg.name);
}

function countItems(value: ArgValue): number {
  return String(value ?? '').split(',').filter((v) => v.trim()).length;
}

/** Initial values from the spec defaults. */
function initialValues(args: CommandArgSpec[]): Record<string, ArgValue> {
  const out: Record<string, ArgValue> = {};
  args.forEach((a) => {
    if (a.type === 'bool') out[a.name] = a.default === true;
    else out[a.name] = a.default === null || a.default === undefined ? '' : String(a.default);
  });
  return out;
}

export function CommandRunForm({ command, submitting, prefill, onRun }: CommandRunFormProps) {
  const [values, setValues] = useState<Record<string, ArgValue>>(
    () => ({ ...initialValues(command.args), ...(prefill ?? {}) }),
  );
  const [confirmText, setConfirmText] = useState('');

  const setValue = useCallback((name: string, value: ArgValue) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const missing = useMemo(
    () => command.args.filter(
      (a) => a.required && (values[a.name] === '' || values[a.name] === undefined),
    ),
    [command.args, values],
  );

  const needsConfirm = command.destructive;
  const confirmed = !needsConfirm || confirmText.trim() === command.key;
  const canRun = missing.length === 0 && confirmed && !submitting;

  /** Run, then disarm.
   *
   *  The typed confirmation and the Apply tick used to survive the run that
   *  consumed them, so every later click on Run was pre-armed — a second click
   *  re-applied a destructive command with no further confirmation. That is how
   *  a capped trial batch turned into an uncapped apply during testing. A
   *  confirmation that outlives its own use is not a confirmation, so both are
   *  cleared here and must be re-entered deliberately for each run. */
  const runAndDisarm = useCallback(() => {
    onRun(values);
    setConfirmText('');
    const applyArg = command.args.find((a) => a.type === 'bool' && a.name === 'apply');
    if (applyArg) setValues((prev) => ({ ...prev, [applyArg.name]: false }));
  }, [onRun, values, command.args]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {command.args.map((arg) => (
          <div
            key={arg.name}
            className={arg.type === 'bool' || isListArg(arg) ? 'col-span-2' : ''}
          >
            {arg.type === 'bool' ? (
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={values[arg.name] === true}
                  onCheckedChange={(c) => setValue(arg.name, c === true)}
                  className="mt-0.5"
                />
                <span>
                  <span className="text-sm font-medium">{arg.label}</span>
                  <code className="ml-2 text-[11px] text-muted-foreground">{arg.flag}</code>
                  {arg.help && (
                    <span className="block text-xs text-muted-foreground">{arg.help}</span>
                  )}
                </span>
              </label>
            ) : (
              <>
                <Label htmlFor={`arg-${arg.name}`} className="text-sm">
                  {arg.label}
                  {arg.required && <span className="text-red-600 ml-0.5">*</span>}
                  <code className="ml-2 text-[11px] font-normal text-muted-foreground">
                    {arg.flag}
                  </code>
                </Label>
                {isListArg(arg) ? (
                  <>
                    <Textarea
                      id={`arg-${arg.name}`}
                      className="mt-1 font-mono text-xs h-24"
                      value={String(values[arg.name] ?? '')}
                      onChange={(e) => setValue(arg.name, e.target.value)}
                      placeholder="Comma-separated ids, or use the handoff button on a result"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      <span className="font-medium tabular-nums">
                        {countItems(values[arg.name]).toLocaleString()}
                      </span>{' '}
                      id{countItems(values[arg.name]) === 1 ? '' : 's'}
                      {values[arg.name] ? '' : ' — nothing will run until this is filled'}
                    </p>
                  </>
                ) : arg.type === 'choice' ? (
                  <Select
                    value={String(values[arg.name] ?? '')}
                    onValueChange={(v) => setValue(arg.name, v)}
                  >
                    <SelectTrigger id={`arg-${arg.name}`} className="mt-1">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {arg.choices.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id={`arg-${arg.name}`}
                    className="mt-1"
                    type={arg.type === 'int' ? 'number' : arg.type === 'date' ? 'date' : 'text'}
                    value={String(values[arg.name] ?? '')}
                    onChange={(e) => setValue(arg.name, e.target.value)}
                  />
                )}
                {arg.help && (
                  <p className="mt-1 text-xs text-muted-foreground">{arg.help}</p>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {needsConfirm && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
          <div className="flex items-center gap-2 text-amber-900 text-sm font-medium">
            <ShieldAlert className="h-4 w-4" />
            This command changes data
          </div>
          <p className="mt-1 text-xs text-amber-900">
            {command.undo_hint
              ? <>Undo: <code className="font-mono">{command.undo_hint}</code></>
              : 'There is no undo for this command.'}
          </p>
          <Label htmlFor="confirm-key" className="mt-3 block text-xs text-amber-900">
            Type <code className="font-mono font-semibold">{command.key}</code> to enable Run
          </Label>
          <Input
            id="confirm-key"
            className="mt-1 bg-white"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={command.key}
          />
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button
          onClick={runAndDisarm}
          disabled={!canRun}
          title={missing.length ? `Required: ${missing.map((m) => m.label).join(', ')}` : undefined}
        >
          {submitting
            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            : <Play className="h-4 w-4 mr-2" />}
          {submitting ? 'Queueing…' : 'Run command'}
        </Button>
        {missing.length > 0 && (
          <span className="text-xs text-muted-foreground">
            Required: {missing.map((m) => m.label).join(', ')}
          </span>
        )}
      </div>
    </div>
  );
}
